(() => {
  if (customElements.get('mini-menu-product-panel')) {
    return;
  }

  class MiniMenuProductPanel extends HTMLElement {
    connectedCallback() {
      this._drawer = this.closest('navigation-drawer');
      this._panelWrapper = this.closest('.mini-menu-product-panel__wrapper');
      this._scrollContainer = this._panelWrapper;
      this._scrollTopButton = this.querySelector('.mini-menu-product-panel__scroll-top');
      this._moreRows = this.querySelector('.mini-menu-product-panel__more-rows');
      this._moreStylesWrap = this.querySelector('.mini-menu-product-panel__more-styles');
      this._moreStylesExpanded = false;
      this._scrollingToTop = false;

      this._onKeydown = this._handleKeydown.bind(this);
      this._onFocusIn = this._handleFocusIn.bind(this);
      this._onScroll = this._handleScroll.bind(this);

      this.querySelectorAll('.mini-menu-product-panel__more-styles-chip').forEach((button) => {
        button.addEventListener('click', () => {
          this._expandMoreStyles();
        });
      });

      this._scrollTopButton?.addEventListener('click', () => {
        this._scrollToTop();
      });

      this._drawerObserver = new MutationObserver(() => this._syncPanelState());
      if (this._drawer) {
        this._drawerObserver.observe(this._drawer, { attributes: true, attributeFilter: ['open'] });
      }

      if (this._panelWrapper) {
        this._wrapperObserver = new MutationObserver(() => this._syncPanelState());
        this._wrapperObserver.observe(this._panelWrapper, { attributes: true, attributeFilter: ['hidden'] });
      }

      this._syncPanelState();
    }

    disconnectedCallback() {
      this._deactivateFocusTrap();
      this._detachScrollListener();
      this._drawerObserver?.disconnect();
      this._wrapperObserver?.disconnect();
    }

    _syncPanelState() {
      const wrapper = this.closest('.panel__wrapper');
      const isOpen = this._drawer?.hasAttribute('open') && wrapper && !wrapper.hasAttribute('hidden');

      if (isOpen) {
        this._activateFocusTrap();
      } else {
        this._deactivateFocusTrap();
        this._resetMoreStyles();
      }
    }

    _expandMoreStyles() {
      this._moreRows?.removeAttribute('hidden');
      this._moreStylesWrap?.setAttribute('hidden', '');
      this._moreStylesExpanded = true;
      this._attachScrollListener();
      this._handleScroll();
    }

    _resetMoreStyles() {
      this._moreRows?.setAttribute('hidden', '');
      this._moreStylesWrap?.removeAttribute('hidden');
      this._moreStylesExpanded = false;
      this._scrollTopButton?.setAttribute('hidden', '');
      this._detachScrollListener();
    }

    _attachScrollListener() {
      if (!this._scrollContainer || this._scrollListenerAttached) {
        return;
      }

      this._scrollContainer.addEventListener('scroll', this._onScroll, { passive: true });
      this._scrollListenerAttached = true;
    }

    _detachScrollListener() {
      if (!this._scrollContainer || !this._scrollListenerAttached) {
        return;
      }

      this._scrollContainer.removeEventListener('scroll', this._onScroll);
      this._scrollListenerAttached = false;
    }

    _handleScroll() {
      if (!this._moreStylesExpanded || !this._scrollContainer || !this._scrollTopButton) {
        return;
      }

      if (this._scrollingToTop) {
        if (this._scrollContainer.scrollTop <= 48) {
          this._scrollingToTop = false;
        } else {
          return;
        }
      }

      if (this._scrollContainer.scrollTop > 48) {
        this._scrollTopButton.removeAttribute('hidden');
      } else {
        this._scrollTopButton.setAttribute('hidden', '');
      }
    }

    _scrollToTop() {
      this._scrollingToTop = true;
      this._scrollTopButton?.setAttribute('hidden', '');
      this._scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    _getFocusableElements() {
      return Array.from(
        this.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);
    }

    _activateFocusTrap() {
      if (this._focusTrapActive) {
        return;
      }

      this._focusTrapActive = true;
      document.addEventListener('keydown', this._onKeydown, true);
      this.addEventListener('focusin', this._onFocusIn);

      const focusable = this._getFocusableElements();
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        this.setAttribute('tabindex', '-1');
        this.focus();
      }
    }

    _deactivateFocusTrap() {
      if (!this._focusTrapActive) {
        return;
      }

      this._focusTrapActive = false;
      document.removeEventListener('keydown', this._onKeydown, true);
      this.removeEventListener('focusin', this._onFocusIn);
    }

    _handleFocusIn(event) {
      if (!this.contains(event.target)) {
        const focusable = this._getFocusableElements();
        if (focusable.length > 0) {
          focusable[0].focus();
        }
      }
    }

    _handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this._closePanel();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = this._getFocusableElements();
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _closePanel() {
      const backButton = this.querySelector('.mini-menu-product-panel__back[data-panel]');
      if (backButton) {
        backButton.click();
      }
    }

  }

  customElements.define('mini-menu-product-panel', MiniMenuProductPanel);
})();
